import React, { useEffect, useMemo, useState } from 'react';
import { DiscountsAPI } from '../api';
import { ServicesAPI, PackagesAPI } from '../api';

export default function AdminDiscounts() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [ds, ss, ps] = await Promise.all([
          DiscountsAPI.list(),
          ServicesAPI.list(),
          PackagesAPI.list(),
        ]);
        setItems(ds);
        setServices(ss);
        setPackages(ps);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const empty = {
    name: '',
    description: '',
    percent: 10,
    validFrom: '',
    validTo: '',
    active: true,
    targetAllServices: false,
    targetAllPackages: false,
    serviceIds: [],
    packageIds: [],
  };

  const startCreate = () => setEditing({ ...empty });
  const startEdit = (d) => setEditing({
    id: d.id,
    name: d.name,
    description: d.description || '',
    percent: Number(d.percent || 0),
    validFrom: d.validFrom ? d.validFrom.substring(0,10) : '',
    validTo: d.validTo ? d.validTo.substring(0,10) : '',
    active: !!d.active,
    targetAllServices: !!d.targetAllServices,
    targetAllPackages: !!d.targetAllPackages,
    serviceIds: (d.Services || []).map(s => s.id),
    packageIds: (d.Packages || []).map(p => p.id),
  });

  const save = async () => {
    const payload = { ...editing };
    if (!payload.name.trim()) return alert('Name erforderlich');
    if (!payload.percent || payload.percent <= 0 || payload.percent > 100) return alert('Prozentsatz (1-100) erforderlich');
    if (payload.id) {
      const res = await DiscountsAPI.update(payload.id, payload);
      setItems(items.map(it => it.id === res.id ? res : it));
    } else {
      const res = await DiscountsAPI.create(payload);
      setItems([res, ...items]);
    }
    setEditing(null);
  };

  const remove = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    await DiscountsAPI.remove(id);
    setItems(items.filter(i => i.id !== id));
  };

  return (
    <div className="home-page">
      {/* Outer full-width header section */}
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Rabatte & Aktionen</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Aktionen verwalten und auf Dienstleistungen & Pakete anwenden.</p>
      </section>
      {/* Centered content container */}
      <section style={{ width: '100%', maxWidth: 900, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div style={{ margin: '12px 0' }}>
          <button onClick={startCreate}>Neu anlegen</button>
        </div>
        {loading && <div>Lade…</div>}
        {!loading && (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign:'left', padding:'6px 8px' }}>Name</th>
                <th style={{ textAlign:'left', padding:'6px 8px' }}>%</th>
                <th style={{ textAlign:'left', padding:'6px 8px' }}>Zeitraum</th>
                <th style={{ textAlign:'left', padding:'6px 8px' }}>Ziel</th>
                <th style={{ textAlign:'left', padding:'6px 8px' }}>Aktiv</th>
                <th style={{ textAlign:'left', padding:'6px 8px' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map(d => (
                <tr key={d.id}>
                  <td style={{ padding:'6px 8px' }}>{d.name}</td>
                  <td style={{ padding:'6px 8px' }}>{Number(d.percent).toFixed(2)}%</td>
                  <td style={{ padding:'6px 8px' }}>{(d.validFrom ? d.validFrom.substring(0,10) : '—')} – {(d.validTo ? d.validTo.substring(0,10) : '—')}</td>
                  <td style={{ padding:'6px 8px' }}>
                    {(d.targetAllServices ? 'alle Dienstleistungen' : (d.Services || []).length + ' DL')}
                    {', '}
                    {(d.targetAllPackages ? 'alle Pakete' : (d.Packages || []).length + ' Pakete')}
                  </td>
                  <td style={{ padding:'6px 8px' }}>{d.active ? 'Ja' : 'Nein'}</td>
                  <td style={{ padding:'6px 8px' }}>
                    <button onClick={() => startEdit(d)}>Bearbeiten</button>
                    <button onClick={() => remove(d.id)} style={{ marginLeft: 8, color:'#d33' }}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editing && (
          <div style={{ marginTop: 16, padding:12, border:'1px solid #444', borderRadius:8 }}>
            <h3>{editing.id ? 'Aktion bearbeiten' : 'Aktion anlegen'}</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <label>
                <div>Name</div>
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
              </label>
              <label>
                <div>Prozent</div>
                <input type="number" min={1} max={100} step={0.1} value={editing.percent} onChange={e => setEditing({ ...editing, percent: Number(e.target.value) })} />
              </label>
              <label>
                <div>Gültig von</div>
                <input type="date" value={editing.validFrom} onChange={e => setEditing({ ...editing, validFrom: e.target.value })} />
              </label>
              <label>
                <div>Gültig bis</div>
                <input type="date" value={editing.validTo} onChange={e => setEditing({ ...editing, validTo: e.target.value })} />
              </label>
              <label style={{ gridColumn:'1 / span 2' }}>
                <div>Beschreibung (optional)</div>
                <textarea rows={3} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </label>
              <label>
                <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Aktiv
              </label>
              <div />
              <label>
                <input type="checkbox" checked={editing.targetAllServices} onChange={e => setEditing({ ...editing, targetAllServices: e.target.checked })} /> Auf alle Dienstleistungen
              </label>
              <label>
                <input type="checkbox" checked={editing.targetAllPackages} onChange={e => setEditing({ ...editing, targetAllPackages: e.target.checked })} /> Auf alle Pakete
              </label>

              {!editing.targetAllServices && (
                <div style={{ gridColumn:'1 / span 2' }}>
                  <div>Dienstleistungen auswählen</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:6 }}>
                    {services.map(s => (
                      <label key={s.id} style={{ border:'1px solid #444', borderRadius:6, padding:'4px 8px' }}>
                        <input type="checkbox" checked={editing.serviceIds.includes(s.id)} onChange={e => {
                          const set = new Set(editing.serviceIds);
                          if (e.target.checked) set.add(s.id); else set.delete(s.id);
                          setEditing({ ...editing, serviceIds: Array.from(set) });
                        }} /> {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {!editing.targetAllPackages && (
                <div style={{ gridColumn:'1 / span 2' }}>
                  <div>Pakete auswählen</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:6 }}>
                    {packages.map(p => (
                      <label key={p.id} style={{ border:'1px solid #444', borderRadius:6, padding:'4px 8px' }}>
                        <input type="checkbox" checked={editing.packageIds.includes(p.id)} onChange={e => {
                          const set = new Set(editing.packageIds);
                          if (e.target.checked) set.add(p.id); else set.delete(p.id);
                          setEditing({ ...editing, packageIds: Array.from(set) });
                        }} /> {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop:12 }}>
              <button onClick={save}>Speichern</button>
              <button onClick={() => setEditing(null)} style={{ marginLeft:8 }}>Abbrechen</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
